import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, ArrowRight, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const industries = [
  "Pharma Retail",
  "BFSI",
  "Healthcare",
  "Retail",
  "IT/SaaS",
  "Manufacturing",
  "Education",
  "Others"
];

const orgSizes = [
  "1–50 employees",
  "51–250 employees",
  "251–1000 employees",
  "1000+ employees"
];

const OrgProfilePage = () => {
  const navigate = useNavigate();
  const { user, orgProfile, assessment, logout, getToken, updateOrgProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    org_name: '',
    industry: '',
    org_size: '',
    city_state: '',
    contact_name: '',
    designation: '',
    phone: '',
    children_data: false,
    cross_border: false,
    sensitive_data: false,
    sdf_status: false
  });

  useEffect(() => {
    if (user?.is_first_login) {
      navigate('/set-password');
      return;
    }

    // Pre-fill from existing data
    if (orgProfile) {
      setFormData(prev => ({
        ...prev,
        ...orgProfile
      }));
    } else if (user) {
      setFormData(prev => ({
        ...prev,
        org_name: user.org_name || '',
        contact_name: `${user.first_name} ${user.last_name}`.trim()
      }));
    }

    // If assessment is in progress or submitted, redirect to appropriate page
    if (assessment?.status === 'in_progress') {
      navigate('/assessment');
    } else if (assessment?.status === 'submitted') {
      navigate('/submission-complete');
    }
  }, [user, orgProfile, assessment, navigate]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.org_name || !formData.industry || !formData.org_size || 
        !formData.city_state || !formData.contact_name || !formData.designation) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/org-profile`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      updateOrgProfile(response.data.profile);
      toast.success('Profile saved successfully');
      navigate('/evidence-discovery');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="CodeXero" className="h-10" />
          <div className="flex items-center gap-4">
            <span className="text-slate-600 text-sm hidden sm:inline">
              {user?.display_username}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-slate-600 hover:text-red-600"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#008080] text-white flex items-center justify-center text-sm font-bold">
                1
              </div>
              <span className="font-medium text-[#003366]">Organisation Profile</span>
            </div>
            <div className="flex-1 h-1 bg-slate-200 rounded"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-sm font-bold">
                2
              </div>
              <span className="text-slate-500">Assessment</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
          <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="org-profile-heading">
            Organisation Profile
          </h1>
          <p className="text-slate-600 mb-8">
            Tell us about your organisation to customise your assessment
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="org_name" className="text-[#003366] font-medium">Organisation Name *</Label>
                <Input
                  id="org_name"
                  data-testid="org-name-input"
                  value={formData.org_name}
                  onChange={(e) => handleChange('org_name', e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="Acme Pvt Ltd"
                />
              </div>

              <div>
                <Label htmlFor="industry" className="text-[#003366] font-medium">Industry *</Label>
                <Select 
                  value={formData.industry} 
                  onValueChange={(value) => handleChange('industry', value)}
                >
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="org_size" className="text-[#003366] font-medium">Organisation Size *</Label>
                <Select 
                  value={formData.org_size} 
                  onValueChange={(value) => handleChange('org_size', value)}
                >
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="org-size-select">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgSizes.map((size) => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="city_state" className="text-[#003366] font-medium">City / State *</Label>
                <Input
                  id="city_state"
                  data-testid="city-state-input"
                  value={formData.city_state}
                  onChange={(e) => handleChange('city_state', e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="Mumbai, Maharashtra"
                />
              </div>

              <div>
                <Label htmlFor="contact_name" className="text-[#003366] font-medium">Name of Person Completing Assessment *</Label>
                <Input
                  id="contact_name"
                  data-testid="contact-name-input"
                  value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="Priya Sharma"
                />
              </div>

              <div>
                <Label htmlFor="designation" className="text-[#003366] font-medium">Designation *</Label>
                <Input
                  id="designation"
                  data-testid="designation-input"
                  value={formData.designation}
                  onChange={(e) => handleChange('designation', e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="Head of Compliance"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="phone" className="text-[#003366] font-medium">Contact Number</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6 mt-8">
              <h2 className="text-lg font-[700] text-[#003366] mb-4">
                Data Processing Questions
              </h2>
              <p className="text-slate-600 text-sm mb-6">
                Your answers will help us customise the assessment questions
              </p>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
                  <div>
                    <p className="font-medium text-[#003366]">Children's Data</p>
                    <p className="text-sm text-slate-600">Does your organisation process personal data of children under 18?</p>
                  </div>
                  <Switch
                    checked={formData.children_data}
                    onCheckedChange={(checked) => handleChange('children_data', checked)}
                    data-testid="children-data-toggle"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
                  <div>
                    <p className="font-medium text-[#003366]">Cross-Border Transfer</p>
                    <p className="text-sm text-slate-600">Does your organisation transfer personal data outside India?</p>
                  </div>
                  <Switch
                    checked={formData.cross_border}
                    onCheckedChange={(checked) => handleChange('cross_border', checked)}
                    data-testid="cross-border-toggle"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
                  <div>
                    <p className="font-medium text-[#003366]">Sensitive Personal Data</p>
                    <p className="text-sm text-slate-600">Does your organisation process sensitive personal data (health, financial, or biometric data)?</p>
                  </div>
                  <Switch
                    checked={formData.sensitive_data}
                    onCheckedChange={(checked) => handleChange('sensitive_data', checked)}
                    data-testid="sensitive-data-toggle"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
                  <div>
                    <p className="font-medium text-[#003366]">Significant Data Fiduciary (SDF)</p>
                    <p className="text-sm text-slate-600">Is your organisation likely to be classified as a Significant Data Fiduciary by MeitY?</p>
                  </div>
                  <Switch
                    checked={formData.sdf_status}
                    onCheckedChange={(checked) => handleChange('sdf_status', checked)}
                    data-testid="sdf-status-toggle"
                  />
                </div>
              </div>
            </div>

            <div className="pt-6">
              <Button
                type="submit"
                disabled={loading}
                data-testid="start-assessment-btn"
                className="w-full bg-[#003366] hover:bg-[#002244] text-white text-lg py-6 rounded-sm btn-hover"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Start Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">© CodeXero {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default OrgProfilePage;
